import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Search,
  Eye,
  Printer,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Package,
  Truck,
  User,
  Calendar,
  Building2,
  ShoppingCart,
  AlertCircle,
  Plus,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useNavigate } from "react-router-dom";
import {
  APL_LINK,
  get_purchase_list,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";
import {
  handleError,
  formatDateStringdot,
} from "../CommonJquery/CommonJquery.js";
import Loader from "../components/layout/Loader.js";

const statusConfig = {
  4: {
    label: "Direct PO (Approved)",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle,
  },
  1: {
    label: "Pending Owner Approval",
    color: "bg-warning/20 text-warning border-warning/30",
    icon: Clock,
  },
  2: {
    label: "Owner Approved",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle,
  },
  3: {
    label: "Rejected",
    color: "bg-destructive/20 text-destructive border-destructive/30",
    icon: XCircle,
  },
  0: {
    label: "Completed",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle,
  },
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dashboard, setdashboard] = useState([]);
  const [mockPOs, setmockPOs] = useState([]);
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);

  // View Dialog State
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingPO, setViewingPO] = useState(null);

  const filteredPOs = mockPOs.filter((po) => {
    const matchesSearch =
      po.purchase_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      po.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || po.approve_status_num === Number(statusFilter);

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    let status = "all";
    let search = "";
    master_data_get(status, search);
  }, []);

  const master_data_get = async (status, search) => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("status", status);
    fd.append("search", search);
    await server_post_data(get_purchase_list, fd)
      .then((Response) => {
        console.log(Response.data.message);
        if (Response.data.error) {
          handleError(Response.data.message);
        } else {
          setmockPOs(Response.data.message.list);
          setdashboard(Response.data.message.dashboard);
        }

        setshowLoaderAdmin(false);
      })
      .catch((error) => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  const getStatusConfig = (status) => {
    const statusKey = status?.toString() || "0";
    return (
      statusConfig[statusKey] || {
        label: "Unknown",
        color: "bg-muted text-muted-foreground",
        icon: FileText,
      }
    );
  };

  const handleViewClick = useCallback((po) => {
    setViewingPO(po);
    setIsViewDialogOpen(true);
  }, []);

  const printExternalPdf = (pdfUrl) => {
    const win = window.open(pdfUrl, "_blank");

    if (!win) {
      handleError("Popup blocked. Please allow popups to print.");
      return;
    }

    const timer = setInterval(() => {
      try {
        if (win.document.readyState === "complete") {
          clearInterval(timer);
          win.focus();
          win.print();
        }
      } catch (e) {
        clearInterval(timer);
      }
    }, 500);
  };

  return (
    <MainLayout>
      <Header
        title="Purchase Orders"
        subtitle="Create, approve & track all purchase orders"
      />
      {showLoaderAdmin && <Loader />}
      <div className="p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground">
                Pending Owner Approval
              </CardDescription>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-warning" />
                {mockPOs.filter((po) => po.approve_status_num === 1).length}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground">
                Total Approved
              </CardDescription>
              <CardTitle className="text-2xl flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success" />
                {
                  mockPOs.filter(
                    (po) =>
                      po.approve_status_num === 0 ||
                      po.approve_status_num === 2,
                  ).length
                }
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground">
                Rejected by Owner
              </CardDescription>
              <CardTitle className="text-2xl flex items-center gap-2">
                <XCircle className="w-5 h-5 text-destructive" />
                {mockPOs.filter((po) => po.approve_status_num === 3).length}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search PO number or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="1">Pending Approval</SelectItem>
                <SelectItem value="0">Direct Approved</SelectItem>
                <SelectItem value="2">Owner Approved</SelectItem>
                <SelectItem value="3">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
            onClick={() => navigate("/create-po")}
          >
            <Plus className="w-4 h-4" />
            Create PO
          </Button>
        </div>

        {/* PO Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {filteredPOs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No purchase orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">
                    PO Number
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Vendor
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground text-center">
                    Items
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Created
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPOs.map((po) => {
                  const statusInfo = getStatusConfig(po.approve_status_num);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <TableRow
                      key={po.id}
                      className="border-border hover:bg-muted/30"
                    >
                      <TableCell className="font-semibold text-primary">
                        {po.purchase_no}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">
                            {po.vendor_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {po.department_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {po.total_items}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "font-medium border gap-1",
                            statusInfo.color,
                          )}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {po.createdAt}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {po.admin_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClick(po)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={
                              po.approve_status_num === 1 ||
                              po.approve_status_num === 3
                            }
                            onClick={() =>
                              printExternalPdf(APL_LINK + po.po_generated_image)
                            }
                            className="hover:bg-primary/10 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* View PO Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Purchase Order Details</DialogTitle>
              <DialogDescription>
                Complete information about this purchase order
              </DialogDescription>
            </DialogHeader>

            {viewingPO && (
              <div className="space-y-6 py-4">
                {/* Header Card */}
                <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                      <ShoppingCart className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {viewingPO.purchase_no}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Purchase Order ID: {viewingPO.id}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Badge
                      className={cn(
                        "font-medium border gap-1",
                        getStatusConfig(viewingPO.approve_status_num).color,
                      )}
                    >
                      {(() => {
                        const StatusIcon = getStatusConfig(
                          viewingPO.approve_status_num,
                        ).icon;
                        return <StatusIcon className="w-3 h-3" />;
                      })()}
                      {getStatusConfig(viewingPO.approve_status_num).label}
                    </Badge>
                  </div>
                </div>

                {/* Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Truck className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Vendor
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingPO.vendor_name || "N/A"}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Department
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingPO.department_name || "N/A"}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Created By
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingPO.admin_name || "N/A"}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Created Date
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {formatDateStringdot(viewingPO.createdAt) || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Items Section - Added Purchase Type & Each Contains */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Order Items ({viewingPO.total_items || 0})
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-muted-foreground">
                            Sr.No
                          </TableHead>
                          <TableHead className="text-muted-foreground">
                            Item Name
                          </TableHead>
                          
                          <TableHead className="text-muted-foreground">
                            Purchase Type
                          </TableHead>
                          <TableHead className="text-muted-foreground">
                            Packs
                          </TableHead>
                          <TableHead className="text-center font-semibold">
                            Qty <br /><i className="font-normal">per Packs</i>
                          </TableHead>
                          <TableHead className="text-muted-foreground">
                            Total
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingPO.items && viewingPO.items.length > 0 ? (
                          viewingPO.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">
                                <div className="flex flex-col">
                                    {/* Main Name: Pehle alter_name, agar nahi hai toh item_name */}
                                    <span className="font-medium text-sm text-foreground">
                                      {item.item_alter_name || item.item_name || "N/A"}
                                    </span>

                                    {/* Secondary Name: Agar dono exist karte hain aur alag hain, toh bracket mein dikhao */}
                                    {item.item_name && 
                                    item.item_alter_name && 
                                    item.item_name?.trim().toLowerCase() !== item.item_alter_name?.trim().toLowerCase() && (
                                      <span className="text-muted-foreground text-[11px] font-normal leading-none mt-0.5">
                                        ({item.item_name})
                                      </span>
                                    )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item.purchase_type || "N/A"}
                              </TableCell>
                              
                              <TableCell>
                                {item.purchase_type?.toLowerCase() !== "loose"
                                  ? item.each_container || "N/A"
                                  : "—"}
                              </TableCell>
                              
                              <TableCell>
                                {item.purchase_qty}   {item.item_unit_type || "Unit"}
                              </TableCell>
                              <TableCell>
                                {item.item_qty_entry}   {item.item_unit_type || "Unit"}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center text-muted-foreground py-8"
                            >
                              No items available in this order
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Remarks Section */}
                {viewingPO.remarks && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Remarks
                    </h4>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <p className="text-sm text-foreground">
                        {viewingPO.remarks}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
              {viewingPO?.approve_status_num === 1 && (
                <p className="text-muted-foreground text-sm font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-warning" />
                  Waiting for approval from Admin
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
